import { getSpritzContract, Network, SupportedNetwork } from '@spritz-finance/sdk'
import { BigNumber, Contract } from 'ethers'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
    TOKEN_APPROVAL_FAILED_EVENT,
    TOKEN_APPROVAL_SUCCEEDED_EVENT,
} from '../../../../../analytics/events'
import { useAnalytics } from '../../../../../context/useAnalytics'
import { ERC20Interface } from '../../../../../web3/hooks/interfaces'
import { TransactionStatus } from '../../../../../web3/hooks/types'
import { useContractFunction } from '../../../../../web3/hooks/useContractFunction'
import useTokenAllowance from '../../../../../web3/hooks/useTokenAllowance'
import { useWeb3Connection } from '../../../../../web3/Web3Provider'
import { isProduction } from '../../../../app/config'
import { logger } from '../../../../app/logger'
import { TokenDetails } from '../balance/BalanceProvider'
import { MAX_ALLOWANCE, TransactionStatusEnum } from '../constants'
import { walletErrorMessage } from '../errors'
import { useGasPrice } from '../gas/getGasPrice'
import { ActionLogItem } from '../useFundDirectPayment'

const networkGasLimit: Record<SupportedNetwork, string | null> = {
    [Network.Polygon]: '80000',
    [Network.Ethereum]: '80000',
    [Network.Binance]: '80000',
    [Network.Avalanche]: '80000',
    [Network.Arbitrum]: null,
    [Network.Optimism]: '80000',
}

const useApproveToken = (
    setActionLog: (log: ActionLogItem) => void,
    contractAddress?: string,
    tokenAddress?: string
) => {
    const { connectedNetwork } = useWeb3Connection()
    const contract = tokenAddress ? new Contract(tokenAddress, ERC20Interface) : null
    const getGasPrice = useGasPrice()
    const { state, send } = useContractFunction(contract, 'approve')

    const approve = async (amount: any = MAX_ALLOWANCE) => {
        if (!tokenAddress || !contractAddress) return

        setActionLog({
            text: 'Initiating transaction',
            isSuccess: true,
        })

        const gasPrice = Math.ceil(await getGasPrice())

        if (gasPrice) console.log('Setting gas price: ', gasPrice)
        const gasLimit = networkGasLimit[connectedNetwork!]

        const args =
            gasPrice && gasLimit
                ? [contractAddress, amount, { gasPrice, gasLimit }]
                : [contractAddress, amount]

        await send(...args)
    }

    return {
        approve,
        approveState: state,
    }
}

const useApprovalState = (
    state: TransactionStatus,
    setActionLog: (log: ActionLogItem) => void,
    tokenSymbol?: string | null
) => {
    const { trackAnalyticsEvent } = useAnalytics()
    useEffect(() => {
        if (!state) return
        switch (state.status as TransactionStatusEnum) {
            case TransactionStatusEnum.PendingSignature:
                setActionLog({
                    text: 'Confirm the approval in your connected wallet...',
                    isSuccess: true,
                })
                break
            case TransactionStatusEnum.Mining:
                setActionLog({
                    text: 'Please wait, approval is being processed...',
                    isSuccess: true,
                })
                break
            case TransactionStatusEnum.Success:
                setActionLog({
                    text: `Approved! You can now pay with ${tokenSymbol}`,
                    isSuccess: true,
                })
                trackAnalyticsEvent(TOKEN_APPROVAL_SUCCEEDED_EVENT, {
                    token: tokenSymbol,
                })
                break
            case TransactionStatusEnum.Exception:
            case TransactionStatusEnum.Fail:
                logger.error(`Approval failed: ${state.errorMessage}`)
                setActionLog({
                    text: 'Unable to complete transaction. Make sure you have enough of this network\'s native token to cover the gas for the transaction. If the problem persists, try setting an allowance using the "Advanced allowance configuration" tool below.',
                    isError: true,
                    isRetriable: true,
                })
                trackAnalyticsEvent(TOKEN_APPROVAL_FAILED_EVENT, {
                    token: tokenSymbol,
                    error: walletErrorMessage({ message: state.errorMessage }),
                })
                break
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state])
}

export const useAllowance = (token?: TokenDetails) => {
    const [actionLog, setActionLog] = useState<ActionLogItem | null>(null)
    const { connectedNetwork } = useWeb3Connection()
    const staging = !isProduction()

    const spritzContract = useMemo(
        () => (connectedNetwork ? getSpritzContract(connectedNetwork, staging) : null),
        [connectedNetwork, staging]
    )

    const { allowance, refetchAllowance, setAllowance } = useTokenAllowance(
        token?.address,
        spritzContract?.address,
        token?.wallet
    )

    const { approve, approveState } = useApproveToken(
        setActionLog,
        spritzContract?.address,
        token?.address
    )
    useApprovalState(approveState, setActionLog, token?.symbol)

    useEffect(() => {
        if (approveState.status === 'Success') {
            const approvalEvent = ((approveState.receipt as any)?.events ?? []).find(
                (e) => e.event === 'Approval'
            )
            const newAllowance = approvalEvent?.args[2] as BigNumber
            if (!newAllowance) return

            setAllowance(newAllowance)
        } else {
            refetchAllowance()
        }
    }, [approveState.receipt, approveState.status, refetchAllowance, setAllowance])

    const deny = async () => {
        approve(0)
    }

    const clearActionLog = useCallback(() => {
        setActionLog(null)
    }, [])

    const allowanceLoading = allowance === undefined

    return {
        connectedNetwork,
        hasAllowance: allowance ? !allowance.isZero() : true,
        allowanceLoading,
        approve,
        approveState,
        approveStatus: approveState.status,
        approvalLoading:
            approveState.status === 'Mining' || approveState.status === 'PendingSignature',
        deny,
        currentAllowance: allowance,
        actionLog,
        refreshAllowance: refetchAllowance,
        clearActionLog,
    }
}
